import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from '../routing';
import { analysisApi, suppliersApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { currentMonthRange, periodLabel, PRESETS_ANALYTIC, readPeriodParams, withPeriodParams } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import { buildClassificationSlices, pctLabel } from '../utils/classificationSlices';
import { classificationColor } from '../utils/classificationColors';
import { useSortable } from '../utils/useSortable';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import { track, EVENTS } from '../utils/analytics';
import Icon from '../components/Icon';
import ExpandCaret from '../components/ExpandCaret';
import PeriodPicker from '../components/PeriodPicker';
import ExportMenu from '../components/ExportMenu';
import InlineLoadError from '../components/InlineLoadError';
import ClassificationSpendEmptyState from '../components/ClassificationSpendEmptyState';
import MultiSelectFilter from '../components/MultiSelectFilter';
import FilterButton from '../components/FilterButton';
import FilterSheet from '../components/FilterSheet';
import ProductDocumentsSheet, { ProductDocumentsPanel } from '../components/ProductDocumentsSheet';
import HqOnlyNotice from '../components/HqOnlyNotice';
import PageViewAsMenu from '../components/ViewAsMenu';

// The full by-classification table — the workbench half of what used to be crammed
// into ClassificationAnalysis. Reached from that card's לצפייה ← footer, which
// forwards the active window (and the supplier filter, when one is on) so the
// two screens describe the same money.
//
// Rules this page keeps that the card cannot:
//
//   לא מסווג IS PINNED, NEVER SORTED. It is not competing for a rank — it is
//   the measure of what the ranking does not cover. Sorting it into third
//   place would invite reading it as "the third biggest classification".
//
//   IT IS SPLIT IN TWO. "ניתן לשיוך" is work someone can do; "לא ניתן לשיוך"
//   is invoice lines with no catalog match, which no amount of tagging fixes.
//   One row for both is why the card's old CTA could never reach ₪0.
//
//   EXPANDING A CLASSIFICATION SHOWS ITS PRODUCTS, not its suppliers. "Where did the
//   ₪30,600 in ירקות go" is a question about what was bought, and a supplier
//   list answers a question nobody asked at this depth.
//
//   THE SEARCH BOX NARROWS PRODUCT ROWS ONLY — see the comment above `term`.

// Every colSpan on this page derives from here. Hand-written ones drifted from
// the header twice while this table was growing, and a row one cell short reads
// as a rendering glitch rather than as something to report.
const COLS = 5;
// The product table inside an open classification: ספק, מק״ט, שם מוצר, כמות,
// סכום, % מהסיווג, מספר מסמכים — the documents sub-lines span all of it.
const DRILL_COLS = 7;

// Group product rows by the classification they belong to. The null bucket is keyed
// 'null' so that rows carrying it land together in one Map entry.
const groupByClassification = (rows) => {
  const m = new Map();
  (rows || []).forEach(r => {
    const k = r.classification_id == null ? 'null' : r.classification_id;
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(r);
  });
  return m;
};

const sumSpend = (rows) => rows.reduce((a, r) => a + (r.spend || 0), 0);
const formatQty = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });

export default function ItemClassificationReportPage() {
  const { canEdit, isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const initial = currentMonthRange();
  // A preset rides the URL as `?period=<key>` and is re-evaluated here, so a
  // bookmark of "90 ימים אחרונים" means the last 90 days, not the 90 days it
  // meant the day it was saved (period.js, withPeriodParams).
  const urlRange = readPeriodParams(searchParams, PRESETS_ANALYTIC);
  const [dateFrom, setDateFrom] = useState(urlRange.from || initial.from);
  const [dateTo, setDateTo] = useState(urlRange.to || initial.to);
  // The key the URL carries, fixed when the range changes; also rolls a preset
  // forward when the tab comes back after the day changed (usePresetPeriod).
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, (f, t) => { setDateFrom(f); setDateTo(t); });
  // Arrives pre-filled from /analysis when a supplier filter was active there,
  // and is editable here. It used to be read-only, with a note apologising for
  // that — which left the one screen that drills deepest as the one screen you
  // could not re-scope.
  const [supplierIds, setSupplierIds] = useState(
    () => (searchParams.get('supplier_ids') || '').split(',').filter(Boolean),
  );
  const [q, setQ] = useState('');

  const [data, setData] = useState({ total: 0, classifications: [] });
  const [baseProducts, setBaseProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // null = no search in effect. An empty ARRAY is a real answer — "nothing
  // matched" — and the two must not collapse into one state.
  const [matchProducts, setMatchProducts] = useState(null);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [drill, setDrill] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // A product's documents (מספר מסמכים): the bottom sheet on a phone, sub-lines
  // under the product row from 641px, one open at a time (owner, 23/10/2026,
  // #318). docsProduct = the row behind an open sheet; docsOpen = the open
  // row's key AND the scope it was opened in (see docsScope below).
  const phone = useIsMobile(640);
  const [docsProduct, setDocsProduct] = useState(null);
  const [docsOpen, setDocsOpen] = useState(null);

  const filterRef = useRef(null);
  useStickyFilterOffset(filterRef);

  // NEVER an empty supplier_ids param. The server reads a present-but-empty
  // list as `s.id IN (-1)` — nothing at all — while omitting it means
  // everything. So "no supplier ticked" has to omit the param, or unticking the
  // last supplier would blank the report instead of widening it.
  const params = useMemo(() => ({
    date_from: dateFrom,
    date_to: dateTo,
    ...(supplierIds.length ? { supplier_ids: supplierIds.join(',') } : {}),
  }), [dateFrom, dateTo, supplierIds]);

  // replace: true — Back should return to /analysis, not step through every
  // period the user tried on the way. `q` is deliberately NOT in the URL: it is
  // a transient lens on one expansion, not part of what this link describes.
  useEffect(() => {
    const next = withPeriodParams(new URLSearchParams(), presetKey, dateFrom, dateTo);
    if (supplierIds.length) next.set('supplier_ids', supplierIds.join(','));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, presetKey, supplierIds]);

  // The full supplier list, never one derived from the loaded rows.
  // ItemClassificationPage documents why: deriving it there dropped 42 real
  // suppliers from the picker, and a filter that cannot name a supplier you buy
  // from reads as "you have no such supplier".
  useEffect(() => {
    suppliersApi.getAll({ include_empty: true })
      .then(r => setAllSuppliers(r.data || []))
      .catch(() => setAllSuppliers([]));
  }, []);

  const key = JSON.stringify(params);

  // Both calls share one invalidation key, so they are fetched and failed
  // together. Letting the products call fail alone would leave the counts
  // column reading 0 beside a classification with real spend — a confident wrong
  // number, which is worse here than a retry prompt.
  const fetchData = useCallback(() => {
    let alive = true;
    setLoading(true);
    setLoadError(false);
    setDrill(null);
    Promise.all([
      analysisApi.getClassificationReport(params),
      analysisApi.getClassificationProducts(params),
    ])
      .then(([report, products]) => {
        if (!alive) return;
        setData(report.data);
        setBaseProducts(products.data || []);
      })
      .catch(() => {
        if (!alive) return;
        setLoadError(true);
        setData({ total: 0, classifications: [] });
        setBaseProducts([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(fetchData, [fetchData]);

  // THE SEARCH NARROWS PRODUCT ROWS ONLY.
  //
  // Classification spend, % מהרכש, the footer, the pinned לא מסווג rows and
  // `coverage` all stay full-period under an active search. `q` does not remove
  // classification rows from the table — it changes what is inside an expansion — so
  // re-denominating them would put the same label on a different number, and
  // "% מהרכש" would silently become "% of the spend matching your search". `coverage` cannot be narrowed honestly at all: "spend on lines
  // matching עגבני that match no catalog row" is a real quantity, but it is no
  // longer *the part of your spend you cannot tag*, which is what the CTA above
  // it promises to drive to ₪0.
  //
  // The residual gap is closed out loud rather than hoped away: every expansion
  // under a search leads with "מוצג n מתוך N", and each product's percentage is
  // of the full classification spend on the row directly above it — so an expansion
  // not summing to 100% is correct signal, not a rounding bug.
  const term = q.trim();

  // An open product's documents belong to the scope they were opened in: a
  // new period, supplier filter, classification or search closes them —
  // new rows, new question. Checked AT RENDER, so the commit that changes the
  // scope never renders the old panel with the new dates (a wasted request,
  // perf review #318); and the stale entry is then DISCARDED, so returning to
  // the old scope — clearing a search — cannot bring the panel back by
  // itself (Codex, PR #320).
  const docsScope = `${JSON.stringify(params)}|${drill?.classificationId ?? ''}|${term}`;
  const isDocsOpen = (key) => !phone && docsOpen?.key === key && docsOpen.scope === docsScope;
  const openDocs = (r, key) => {
    if (phone) { setDocsProduct(r); return; }
    setDocsOpen(isDocsOpen(key) ? null : { key, scope: docsScope });
  };
  useEffect(() => {
    setDocsOpen(cur => (cur && cur.scope !== docsScope ? null : cur));
  }, [docsScope]);
  const matchKey = `${key}|${term}`;
  // Guard on the search request in flight. Typing "עגב" then "עגבנ" can land the
  // slower first response last and paint its rows under the newer term — the
  // wrong data under the right title, which reads as fact.
  const queryKeyRef = useRef('');

  useEffect(() => {
    if (!term) {
      queryKeyRef.current = '';
      setMatchProducts(null);
      return undefined;
    }
    queryKeyRef.current = matchKey;
    // Debounced: this endpoint aggregates spend and the server is synchronous.
    const t = setTimeout(() => {
      analysisApi.getClassificationProducts({ ...params, q: term })
        .then(r => { if (queryKeyRef.current === matchKey) setMatchProducts(r.data || []); })
        .catch(() => { if (queryKeyRef.current === matchKey) setMatchProducts([]); });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey]);

  const { total: classifiedTotal, hasData } = buildClassificationSlices(data.classifications);
  const coverage = data.coverage;
  const period = periodLabel(dateFrom, dateTo);

  const baseByCat = useMemo(() => groupByClassification(baseProducts), [baseProducts]);
  const matchByCat = useMemo(
    () => (matchProducts ? groupByClassification(matchProducts) : null),
    [matchProducts],
  );

  // `taggableOnly` serves the ניתן לשיוך row: that row is the work someone can
  // actually do, so it must not expand to lines with no catalog row to tag.
  const productsFor = useCallback((source, classificationId, taggableOnly) => {
    const rows = source ? (source.get(classificationId == null ? 'null' : classificationId) || []) : [];
    return taggableOnly ? rows.filter(r => r.catalog_id != null) : rows;
  }, []);

  // Real classifications only. The null row is rebuilt below from `coverage`, which
  // is the only place the two halves of "לא מסווג" are told apart.
  //
  // The two counts are COUNTED OFF THE PRODUCT ROWS, not served as SQL columns.
  // They used to be columns, and product_count was COUNT(DISTINCT catalog id) —
  // which ignores NULLs, so a line matching no catalog row scored 0 in the
  // column while still appearing in the expansion under it. Counting the very
  // array the expansion renders makes the two structurally unable to disagree.
  // They stay full-period under a search, like every other classification-row figure.
  const classified = useMemo(
    () => (data.classifications || [])
      .filter(c => c.classification_id != null)
      .map(c => {
        const rows = baseByCat.get(c.classification_id) || [];
        return {
          ...c,
          supplier_count: new Set(rows.map(r => r.supplier_id)).size,
          product_count: rows.length,
        };
      }),
    [data.classifications, baseByCat],
  );

  // Colour is a classification's IDENTITY, so it must not move when the table is
  // re-sorted. `classificationColor` falls back to a palette entry indexed by
  // position, so feeding it the current sort index gave an uncoloured classification
  // a different swatch per sort — and a different one again from the card.
  // Keyed once by spend-desc, which is exactly the order the card ranks in.
  const colorById = useMemo(() => {
    const m = new Map();
    [...classified]
      .sort((a, b) => (b.spend || 0) - (a.spend || 0))
      .forEach((c, i) => m.set(c.classification_id, classificationColor(c, i)));
    return m;
  }, [classified]);

  const { sorted, indicator, headerProps } = useSortable(classified, {
    name: r => r.name,
    spend: r => r.spend,
    suppliers: r => r.supplier_count,
    products: r => r.product_count,
  });
  const sortTh = (field, label) => (
    <th {...headerProps(field)}>{label} {indicator(field)}</th>
  );

  // A classification with no product matching the search keeps its row — removing it
  // would change the footer it feeds — but stops being expandable, because
  // there is nothing behind it to open. Only once a search has actually
  // ANSWERED: while one is in flight `matchProducts` is null and every row
  // stays as it was.
  const isEmptyUnderSearch = (classificationId, taggableOnly) => (
    Boolean(term) && matchProducts != null
    && productsFor(matchByCat, classificationId, taggableOnly).length === 0
  );

  // Single-open, as before. Multi-open would need drill→Set and drillRows→Map,
  // and several 116-row expansions at once is a scroll disaster.
  const toggleDrill = (next) => {
    setDrill(prev => (prev && prev.key === next.key ? null : next));
    // Collapsing (or switching) a classification closes its products'
    // documents too — reopening it starts closed (#318).
    setDocsOpen(null);
  };

  // ONE denominator for the whole column: total purchases in the period.
  //
  // The classification rows used to divide by CLASSIFIED spend while the two
  // לא מסווג rows divided by the total, under a header that named only the first
  // ("% מהרכש המסווג") while those rows carried their own "מהסה״כ" suffix. Both
  // were individually honest and the column still read as one, so the eye could
  // add 19.8% to 58% and get 158%. Owner's call: one label, "% מהרכש", which
  // only stays true if there is one denominator behind it.
  //
  // The consequence is deliberate and visible: a classification's share is now
  // of ALL purchases, not of the tagged subset, so it reads SMALLER wherever
  // tagging is incomplete (Basta: דגים 19.8% → 8.3%, because 58% of its spend is
  // untagged). That is the honest number for a column called "% מהרכש", and it
  // makes the untagged rows comparable to the rest instead of a separate scale.
  const pctOfTotal = (spend) => (data.total > 0 ? (spend / data.total) * 100 : 0);

  const breadcrumb = (
    <div className="breadcrumb">
      <Link className="link" to="/analysis">ניתוח רכש</Link>
      <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
      <span>רכש לפי סיווג פריטים</span>
    </div>
  );

  // Rows come from the products payload already in hand — no per-expansion
  // request, so opening a classification is instant and cannot race.
  const drillPanel = () => {
    const baseRows = productsFor(baseByCat, drill.classificationId, drill.taggableOnly);
    const rows = term ? productsFor(matchByCat, drill.classificationId, drill.taggableOnly) : baseRows;
    // Denominated on the FULL classification spend — the row directly above this
    // panel — search or no search. See the `term` comment.
    const denom = drill.denom || 0;
    const searchPending = Boolean(term) && matchProducts == null;

    return (
      <tr>
        <td colSpan={COLS} style={{ background: '#fafbfc', padding: '12px 18px' }}>
          {searchPending ? (
            <div className="loading-container" style={{ minHeight: 80 }}><div className="spinner" /></div>
          ) : rows.length === 0 ? (
            <p style={{ margin: 0, color: '#94a3b8', fontSize: 12.5 }}>
              {term ? 'אין מוצרים התואמים את החיפוש בסיווג זה.' : 'אין מוצרים להצגה בסיווג זה.'}
            </p>
          ) : (
            <>
              {term && (
                <p style={{ margin: '0 0 8px', color: '#94a3b8', fontSize: 12 }}>
                  מוצג {rows.length} מתוך {baseRows.length} מוצרים
                  {' · '}
                  {formatCurrency(sumSpend(rows))} מתוך {formatCurrency(sumSpend(baseRows))}
                </p>
              )}
              {/* Every row is rendered — no paging, no "הצג עוד". The one
                  expansion long enough to tempt it is לא מסווג, and this page
                  family's contract is to revisit that with virtualization, not
                  paging. .nested-scroll-table is the containment: 65vh with its
                  own scroll. The inner .table-scroll-mobile wrapper is what
                  keeps a 7-column table from blowing out the parent width on a
                  phone — the outer wrapper's min-width rule governs the
                  page-level table only. */}
              <div className="nested-scroll-table">
                <div className="table-scroll-mobile">
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>ספק</th>
                        <th>מק״ט</th>
                        <th>שם מוצר</th>
                        <th>כמות</th>
                        <th>סכום (ללא מע״מ)</th>
                        <th>% מהסיווג</th>
                        <th>מספר מסמכים</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const key = `${r.supplier_id}|${r.catalog_id == null ? 'n' : r.catalog_id}|${r.sku || r.name}`;
                        const docsOpen = isDocsOpen(key);
                        return (
                        <Fragment key={key}>
                        <tr style={docsOpen ? { background: 'var(--selected-soft-bg)' } : undefined}>
                          <td>{r.supplier_name}</td>
                          <td style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{r.sku || '—'}</td>
                          <td style={{ fontWeight: 600 }}>{r.name}</td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatQty(r.qty)}</td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.spend)}</td>
                          <td>{denom > 0 ? pctLabel((r.spend / denom) * 100) : '—'}</td>
                          <td>
                            {/* A button, not a link: it opens the documents
                                in place (sub-lines, or the sheet on a phone)
                                rather than navigating. stopPropagation is
                                defensive — the classification's own onClick
                                is on the row ABOVE this drill, not an
                                ancestor of it.
                                `.link` is scoped to .breadcrumb elsewhere in
                                this codebase, so the affordance is inlined
                                here instead of borrowing that class. */}
                            <button
                              type="button"
                              style={{
                                font: 'inherit', fontVariantNumeric: 'tabular-nums',
                                background: 'none', border: 'none', padding: 0,
                                color: '#0f3460', textDecoration: 'underline',
                                textUnderlineOffset: 2, cursor: 'pointer',
                              }}
                              onClick={(e) => { e.stopPropagation(); openDocs(r, key); }}
                              aria-expanded={phone ? undefined : docsOpen}
                            >
                              {r.doc_count}
                            </button>
                            {!phone && <> <ExpandCaret open={docsOpen} size={11} /></>}
                          </td>
                        </tr>
                        {docsOpen && (
                          <tr className="pdocs-row">
                            <td colSpan={DRILL_COLS}><ProductDocumentsPanel product={r} params={params} /></td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </td>
      </tr>
    );
  };

  // The grey bucket comes from the CLASSIFICATION ROWS, not from `coverage`.
  //
  // The server omits `coverage` in the admin cross-tenant view (the scan is too
  // expensive there), and deriving these rows from it meant the whole לא מסווג
  // line disappeared in that view — while the footer went on counting it, so
  // the visible rows silently fell short of the stated total by 63% of spend on
  // real data. `coverage` is now used only to SPLIT the bucket into its
  // reachable and unreachable halves; when it is missing the bucket is still
  // shown, just undivided.
  const untaggedRow = (data.classifications || []).find(c => c.classification_id == null);
  const untaggedTotal = untaggedRow ? untaggedRow.spend : 0;
  const untaggedRows = coverage
    ? [
      { key: 'untagged', label: 'ניתן לשיוך', spend: coverage.untagged, drillable: true, taggableOnly: true },
      { key: 'untaggable', label: 'לא ניתן לשיוך', spend: coverage.untaggable, drillable: false, taggableOnly: false },
    ].filter(r => r.spend > 0)
    : (untaggedTotal > 0
      ? [{ key: 'untagged', label: null, spend: untaggedTotal, drillable: true, taggableOnly: false }]
      : []);

  // ── filter bar ────────────────────────────────────────────────────────────
  // Page-local, in the idiom of the five existing bars rather than through
  // AnalysisFilterBar, which is hard-wired to two text inputs and owns its own
  // PeriodPicker.

  // Memoized on `allSuppliers`. Without this the option list and the id→name
  // map are fresh objects every render, which invalidates MultiSelectFilter's
  // own memos and re-runs its typeahead filter on each keystroke elsewhere.
  const supplierIdToName = useMemo(
    () => new Map(allSuppliers.map(s => [String(s.id), s.name])),
    [allSuppliers],
  );
  const supplierOptions = useMemo(
    () => allSuppliers.map(s => String(s.id)),
    [allSuppliers],
  );
  const renderSupplierOption = useCallback(
    (id) => supplierIdToName.get(id) || id,
    [supplierIdToName],
  );

  const searchPill = (
    <label className={`docs-filter-control docs-filter-searchpill${q ? ' is-active' : ''}`}>
      <Icon name="search" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="שם מוצר או מק״ט…"
        aria-label="חיפוש מוצר"
        style={{ minWidth: isMobile ? 0 : 180 }}
      />
    </label>
  );
  const supplierSelect = (inline) => (
    <MultiSelectFilter
      inline={inline}
      label="ספק"
      values={supplierIds}
      options={supplierOptions}
      onChange={setSupplierIds}
      renderOption={renderSupplierOption}
      searchable
      minSearchChars={2}
    />
  );
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom} dateTo={dateTo}
      onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
      presets={PRESETS_ANALYTIC}
      variant={variant}
    />
  );

  const hasChips = supplierIds.length > 0 || Boolean(q);
  const clearAll = () => { setSupplierIds([]); setQ(''); };
  const renderChips = () => (
    <div className="docs-filter-chips">
      {supplierIds.length > 0 && (
        <span className="docs-filter-chip">
          ספקים: {supplierIds.map(id => supplierIdToName.get(id) || id).join(', ')}
          <button
            type="button"
            aria-label="נקה סינון ספקים"
            onClick={() => setSupplierIds([])}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {q && (
        <span className="docs-filter-chip">
          מוצר: {q}
          <button
            type="button"
            aria-label="נקה חיפוש מוצר"
            onClick={() => setQ('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={clearAll}>
        נקה הכל
      </button>
    </div>
  );

  // ≤720px the bar collapses to the product search plus a "סינון" trigger; the
  // supplier picker moves into a FilterSheet and the date control gets its own
  // sticky row. A filter that only exists above 720px is a broken filter.
  const filterBar = isMobile ? (
    <>
      <div className="period-bar" ref={filterRef}>{dateRange('bar')}</div>
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {searchPill}
          <FilterButton
            activeCount={supplierIds.length > 0 ? 1 : 0}
            expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          />
        </div>
        {hasChips && renderChips()}
        <FilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          resultCount={sorted.length + untaggedRows.length}
          onClearAll={clearAll}
        >
          {supplierSelect(true)}
        </FilterSheet>
      </div>
    </>
  ) : (
    <div className="docs-filter-wrap" ref={filterRef}>
      <div className="docs-filter-bar">
        {searchPill}
        {supplierSelect(false)}
        {dateRange('pill')}
      </div>
      {hasChips && renderChips()}
    </div>
  );

  return (
    <div className="page">
      {breadcrumb}

      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">רכש לפי סיווג פריטים</h1>
          {data.hidden_hq && <HqOnlyNotice count={data.hidden_hq.supplier_count} amount={data.hidden_hq.amount} pending={loading} basis="רכש בתקופה (ללא מע״מ)" />}
        </div>
        <div className="page-title-actions">
          <ExportMenu
            scopeLabel={period}
            options={[{
              key: 'classifications',
              label: 'רכש לפי סיווג פריטים',
              hint: 'שורה לכל סיווג, כולל "לא מסווג", ומגמה חודשית',
              run: () => {
                track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'categories' });
                return analysisApi.exportClassifications(params);
              },
            }, {
              key: 'classification_products',
              label: 'רכש לפי פריטים בסיווג',
              hint: 'שורה לכל מוצר אצל כל ספק, כולל % מהסיווג, וגיליון סיכום לפי סיווג',
              run: () => {
                track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'category_products' });
                // The search travels with the file exactly as it behaves on
                // screen: it narrows the product sheet and leaves the classification
                // sheet whole.
                return analysisApi.exportClassificationProducts({ ...params, ...(term ? { q: term } : {}) });
              },
            }]}
          />
          <PageViewAsMenu />
        </div>
      </div>

      {filterBar}


      <div className="card">
        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את נתוני הסיווגים" onRetry={fetchData} />
        ) : !hasData && untaggedRows.length === 0 ? (
          <ClassificationSpendEmptyState
            period={period}
            total={data.total}
            untaggedSpend={coverage ? coverage.untagged : data.total}
            canEdit={canEdit}
            isAdmin={isAdmin}
          />
        ) : (
          <div className="table-wrapper table-scroll-mobile">
            <table>
              <thead>
                <tr>
                  {sortTh('name', 'סיווג פריט')}
                  {sortTh('spend', 'סכום (ללא מע״מ)')}
                  <th>% מהרכש</th>
                  {sortTh('suppliers', 'ספקים')}
                  {sortTh('products', 'מוצרים')}
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const rowKey = `cat:${c.classification_id}`;
                  const noMatches = isEmptyUnderSearch(c.classification_id, false);
                  const open = !noMatches && !!drill && drill.key === rowKey;
                  return (
                    <Fragment key={c.classification_id}>
                      <tr
                        className={noMatches ? undefined : 'row-clickable'}
                        onClick={noMatches ? undefined : () => toggleDrill({
                          key: rowKey, classificationId: c.classification_id, taggableOnly: false, denom: c.spend,
                        })}
                        style={{ background: open ? '#eaf0f8' : undefined }}
                      >
                        <td style={{ fontWeight: 600 }}>
                          {!noMatches && <><ExpandCaret open={open} />{' '}</>}
                          <span style={{
                            display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                            background: colorById.get(c.classification_id), marginInlineStart: 4,
                          }} />
                          {' '}{c.name}
                          {noMatches && (
                            <span style={{ marginInlineStart: 8, color: '#94a3b8', fontSize: 12, fontWeight: 400 }}>
                              אין התאמות
                            </span>
                          )}
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(c.spend)}</td>
                        <td>{pctLabel(pctOfTotal(c.spend))}</td>
                        <td>{c.supplier_count}</td>
                        <td>{c.product_count}</td>
                      </tr>
                      {open && drillPanel()}
                    </Fragment>
                  );
                })}

                {/* Pinned. Never sorted into the ranking above — see the header
                    comment. */}
                {untaggedRows.map(r => {
                  const rowKey = `bucket:${r.key}`;
                  const noMatches = r.drillable && isEmptyUnderSearch(null, r.taggableOnly);
                  const clickable = r.drillable && !noMatches;
                  const open = clickable && !!drill && drill.key === rowKey;
                  return (
                    <Fragment key={r.key}>
                      <tr
                        className={clickable ? 'row-clickable' : undefined}
                        onClick={clickable ? () => toggleDrill({
                          key: rowKey, classificationId: null, taggableOnly: r.taggableOnly, denom: r.spend,
                        }) : undefined}
                        style={{ background: '#fafbfc' }}
                      >
                        <td style={{ fontWeight: 600, color: 'var(--ink-muted)' }}>
                          {clickable && <><ExpandCaret open={open} />{' '}</>}
                          <span style={{
                            display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                            background: '#D9D9D9', marginInlineStart: 4,
                          }} />
                          {' '}לא מסווג
                          {/* `badge` carries the pill geometry, `badge-gray` only
                              the colours — the modifier alone renders as bare
                              text on a grey rectangle. */}
                          {r.label && (
                            <span className="badge badge-gray" style={{ marginInlineStart: 8, fontSize: 11 }}>{r.label}</span>
                          )}
                          {noMatches && (
                            <span style={{ marginInlineStart: 8, color: '#94a3b8', fontSize: 12, fontWeight: 400 }}>
                              אין התאמות
                            </span>
                          )}
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--ink-muted)' }}>{formatCurrency(r.spend)}</td>
                        <td style={{ color: '#94a3b8' }}>{pctLabel(pctOfTotal(r.spend))}</td>
                        <td colSpan={COLS - 3} style={{ color: '#94a3b8', fontSize: 12 }}>
                          {/* No label = the undivided bucket (no coverage block),
                              so it must not claim either half's explanation. */}
                          {r.label === null
                            ? 'מוצרים ללא סיווג, ושורות שלא נמצא להן מוצר תואם בקטלוג'
                            : r.drillable
                              ? 'מוצרים שקיימים בקטלוג ועדיין ללא סיווג'
                              : 'שורות בחשבונית שלא נמצא להן מוצר תואם בקטלוג'}
                        </td>
                      </tr>
                      {open && drillPanel()}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 700 }}>סה״כ רכש מסווג</td>
                  <td style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(classifiedTotal)}</td>
                  <td colSpan={COLS - 2} style={{ color: '#94a3b8', fontSize: 12 }}>
                    סה״כ רכש בתקופה (כולל לא מסווג): {formatCurrencyWhole(data.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {phone && docsProduct && (
        <ProductDocumentsSheet
          product={docsProduct}
          params={params}
          onClose={() => setDocsProduct(null)}
        />
      )}
    </div>
  );
}
